import { effect, Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { io, Socket } from 'socket.io-client';
import { environment } from '../../../environments/environment';
import {
  AskQuestionModel,
  ChatResponseModel,
  messages,
} from '../../model/chat.model';
import {
  MyBagModel,
  MyBagResponseModel,
  MyBagUserInfo,
} from '../../model/my-bag.model';
import { SharedService } from '../../shared/services/shared.service';
import { platformBrowser } from '@angular/platform-browser';
import { isPlatformBrowser } from '@angular/common';
import { HandicapData } from '../../model/handicap.model';

@Injectable({
  providedIn: 'root',
})
export class ChatService {
  private socket: Socket | null = null;
  private hasListenReportEvent = false;
  private messagesSubject = new Subject<ChatResponseModel>();
  private endMessagesSubject = new Subject<string>();

  private errorLogged = false; // Flag to control error logging
  private audioQueue: string[] = [];
  private isPlaying: boolean = false;
  private currentAudio: HTMLAudioElement | null = null;
  private mimeType: string = 'audio/wav'; // Adjust MIME type if necessary
  private audioMessageEnded = new Subject<boolean>();
  private avoidingUuid: string[] = [];
  private audioStreamingEnded = false;
  private latitude: string | null = null;
  private longitude: string | null = null;
  private myBagResponseData = new Subject<MyBagModel[]>();
  private answerError = new Subject<string>();
  private myBagError = new Subject<string>();
  private isMyBagProcessRunning = new Subject<boolean>();
  private onlockCaddy = new Subject<any>();
  private isEmbedToken = '';
  isSignedIn = false;
  constructor(
    private http: HttpClient,
    private _sharedService: SharedService,
    @Inject(PLATFORM_ID) private platform: object
  ) {
    effect(() => {
      this.isSignedIn = this._sharedService.loggedIn();
    });
   }

  private initializeSocketListeners(): void {
    if (this.socket) {
      // Clean up existing listeners before re-adding
      this.socket.off('receiveAnswer');
      this.socket.off('text-to-audio');
      this.socket.off('receiveAnswerEnd');
      this.socket.off('disconnect');
      this.socket.off('answer');
      this.socket.off('get_my_bag');

      // Register listeners after cleaning up
      this.socket.on('receiveAnswer', (message: ChatResponseModel) => {
        if (message.event !== 'ANSWER_INFO') {
          this.messagesSubject.next(message);
        }
        // this.messagesSubject.next(message);
      });

      this.socket.on('text-to-audio', (message: any) => {
        if (message.event === 'ANSWER_START') {
          this.audioStreamingEnded = false;
        } else if (message.event === 'ANSWER_END') {
          this.audioStreamingEnded = true;
          // this.audioMessageEnded.next(true);
        } else if (
          message.event === 'ANSWER_VOICE_CHUNK' &&
          !this.avoidingUuid.includes(message.id)
        ) {
          this.audioMessageEnded.next(false);
          this.playAudio(message.data);
        } else if (message.event == 'ANSWER_ERROR' || message.event === 'error') {
          this.answerError.next(message.data);
        }
      });

      this.socket.on('receiveAnswerEnd', () => {
        this.endMessagesSubject.next('END');
      });
      this.socket.on('disconnect', () => {
        console.log('Disconnected from WebSocket server');
      });

      // for voice to voice
      this.socket.on('answer', (message: ChatResponseModel) => {
        // if(message.event !== "ANSWER_INFO"){
        this.messagesSubject.next(message);
        // }
        // if(message.event === 'ANSWER_START' || message.event == "ANSWER_CHAT_ID"  || message.event  == "ANSWER_ERROR" || message.event ==="ANSWER_END" || message.event ==="ANSWER_VOICE_CHUNK"){
        //  this.audioResponseData.next(message);
        // }
      });
      // my bag response
      this.socket.on('get_my_bag', (message: any) => {
        if (message.event == 'MY_BAG_DATA') {
          this.myBagResponseData.next(message.data);
        } else if (message.event == 'MY_BAG_ERROR') {
          this.myBagError.next(message.data);
        }
      });
      this.socket.on('is_my_bag_process_running', (message: boolean) => {
        this.isMyBagProcessRunning.next(message);
      })
      this.socket.on('special_caddy', (message: any) => {
        this.onlockCaddy.next(message);
      })
    }
  }

  public onListenWebsocket(): Promise<void> {
    return new Promise((resolve, reject) => {
      const token = localStorage.getItem('access_token');
      if (!token) {
        this.isEmbedToken = this._sharedService.verificationToken();
      }
      if (!this.socket) {
        this.webSocketCreate(token ? token : '')
          .then(() => {
            resolve();
          })
          .catch((error) => {
            reject(error);
          });
      } else {
        resolve(); // WebSocket already exists
      }

      setTimeout(() => {
        if (!this.hasListenReportEvent && this.socket) {
          this.hasListenReportEvent = true;
        }
      }, 0);
    });
  }

  private webSocketCreate(token: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const authData: any = {};
      if (token) {
        authData.token = token;
      }
      if (this.isEmbedToken) {
        authData.verification = this.isEmbedToken;
      }
      this.latitude = this._sharedService.getLocalStorage('latitude');
      this.longitude = this._sharedService.getLocalStorage('longitude');
      if (this.longitude && this.latitude) {
        authData.userLocation = `${this.latitude},${this.longitude}`;
      }
      if (this.socket) {
        this.socket.disconnect();  // Clean up old socket connection if it exists
        this.socket.off();  // Remove all existing event listeners to avoid duplication
      }

      this.socket = io(environment.webUrl, {
        auth: authData,
        transports: ['websocket'],
      });

      this.socket.on('connect', () => {
        resolve();
        this.initializeSocketListeners(); // Initialize listeners on successful connection
      });

      this.socket.on('connect_error', (error: Error) => {
        console.error('WebSocket connection error:', error);
        if (!this.errorLogged) {
          this.errorLogged = true; // Set the flag to true to prevent further logging
        }
        reject(error);
      });

      this.socket.on('error', (error: Error) => {
        console.error('WebSocket error:', error);
        if (!this.errorLogged) {
          this.errorLogged = true; // Set the flag to true to prevent further logging
        }
        reject(error);
      });
    });
  }

  public webSocketGetInstance(): Socket | null {
    return this.socket;
  }

  public disconnectWebSocket(): void {
    if (this.socket) {
      try {
        this.socket.disconnect();
      } catch (error) {
        console.error('Error during socket disconnection:', error);
      } finally {
        this.socket = null;
        this.hasListenReportEvent = false;
      }
    }
  }

  startChat(question: string): Observable<any> {
    return this.http.post(environment.apiUrl + 'chat', {
      question,
    });
  }

  askQuestion(chatId: string, question: string, regenerate: boolean, behaviour: string | undefined) {
    const selectedLangAndCaddy = this._sharedService.selectedCaddyAndLanguage();
    const messageBody: any = { chatId, question, regenerate, behaviour };
    if (selectedLangAndCaddy) {
      messageBody.caddy = selectedLangAndCaddy.caddy;
      messageBody.language = selectedLangAndCaddy.language;
    }
    if (this.socket) {
      try {
        this.socket.emit('askQuestion', messageBody);
      } catch (error) {
        console.error('Error during askQuestion:', error);
      }
    }
  }

  askPublicQuestion(question: string, regenerate: boolean, behaviour: string | undefined) {
    const selectedLangAndCaddy = this._sharedService.selectedCaddyAndLanguage();
    const messageBody: any = { question, regenerate, behaviour };
    if (selectedLangAndCaddy) {
      messageBody.caddy = selectedLangAndCaddy.caddy;
      messageBody.language = selectedLangAndCaddy.language;
    }
    if (this.socket) {
      try {
        this.socket.emit('askPublicQuestion', messageBody);
      } catch (error) {
        console.error('Error during ask Public Question:', error);
      }
    }
  }

  getDiscoverResponse(
    context: string,
    module: string,
    prompt: string,
    playerName: string,
    playerId: string,
    tournamentName: string,
    tournId: string,
    round: string,
    timePeriod = ''
  ) {
    const selectedLangAndCaddy = this._sharedService.selectedCaddyAndLanguage();
    let messageBody: any = {
      context,
      tab: module === 'owgr_rankings' ? 'leaderboard' : 'scorecard',
      module, prompt, playerName, playerId, tournamentName, tournId, round, timePeriod
    };
    // Remove properties that are empty strings or undefined
    messageBody = Object.fromEntries(
      Object.entries(messageBody).filter(([_, value]) => value !== '' && value !== undefined)
    );
    if (selectedLangAndCaddy) {
      messageBody.caddy = selectedLangAndCaddy.caddy;
      messageBody.language = selectedLangAndCaddy.language;
    }

    if (this.socket) {
      try {
        this.socket.emit('discovery', messageBody);
      } catch (error) {
        console.error('Error during askQuestion:', error);
      }
    }
  }

  askQuestionV2(question: AskQuestionModel) {
    // const messageBody = { chatId, question, regenerate };
    if (this.socket) {
      try {
        this.socket.emit('question', question);
      } catch (error) {
        console.error('Error during askQuestion:', error);
      }
    }
  }
  voiceToText(audioUrl: string | undefined) {
    // const messageBody = { chatId, question, regenerate };
    if (this.socket) {
      try {
        this.socket.emit('question',
          {
            processType: 'TS-ATT',
            audioQuestion: audioUrl,
          }
        );
      } catch (error) {
        console.error('Error during askQuestion:', error);
      }
    }
  }
  askVoiceToVoiceQuestion(
    chatId: string,
    question: string,
    regenerate: boolean
  ) {
    const messageBody = { chatId, question, regenerate };
    if (this.socket) {
      try {
        this.socket.emit('askQuestion', messageBody);
      } catch (error) {
        console.error('Error during askQuestion:', error);
      }
    }
  }

  stopThisAudio(uuid: any) {
    this.avoidingUuid.push(uuid);
    this.stopAudio();
  }

  textToAudio(text: string, id: string) {
    const selectedLangAndCaddy = this._sharedService.selectedCaddyAndLanguage();
    const messageBody: any = { text, id };
    if (selectedLangAndCaddy && selectedLangAndCaddy.caddy) {
      messageBody.caddy = { id: selectedLangAndCaddy.caddy.id, code: selectedLangAndCaddy.caddy.code };
      messageBody.language = selectedLangAndCaddy.language;
    }
    if (this.socket) {
      try {
        this.socket.emit('TTA', messageBody);
      } catch (error) {
        console.error('Error during askQuestion:', error);
      }
    }
  }
  // my bag
  uploadMyBag(mediaUrl: string, userInfo: MyBagUserInfo | null = null) {
    if (this.socket) {
      try {
        if (userInfo) {
          this.socket.emit('set_my_bag', {
            mediaURL: mediaUrl,
            userInfo: userInfo,
          });
        } else {
          this.socket.emit('set_my_bag', { mediaURL: mediaUrl });
        }
      } catch (error) {
        console.error('Error during UploadMyBag:', error);
      }
    }
  }
  // Golf Digest - My Bag
  uploadGolfDigestMyBag(
    mediaUrl: string,
    handicap: string,
    roundsPerYear: string,
    age: string,
    gender: string,
    unit: string,
    clubDistanceList: HandicapData[]
  ) {
    if (this.socket) {
      try {
        const mappedClubDistanceList = clubDistanceList.reduce((acc: { [key: string]: number }, curr) => {
          // Use club as key and distance as value
          acc[curr.club] = curr.distance;
          return acc;
        }, {});
        this.socket.emit('set_golf_digest_my_bag', {
          mediaURL: mediaUrl,
          handicap: handicap,
          roundsPerYear: roundsPerYear,
          ageRange: age,
          gender,
          distanceUnit: unit,
          clubDistances: mappedClubDistanceList
        });
      } catch (error) {
        console.error('Error during UploadGDMyBag:', error);
      }
    }
  }
  // Audio Section starts here

  playAudio(base64Data: string) {
    this.audioQueue.push(base64Data);
    if (!this.isPlaying) {
      this.playNextAudioChunk();
    }
  }

  /**
   * Plays the next audio chunk in the queue.
   */
  private playNextAudioChunk() {
    if (this.audioQueue.length > 0) {
      const base64Data = this.audioQueue.shift(); // Get the next audio chunk
      if (base64Data) {
        this.playAudioFromBase64(base64Data);
      }
    } else if (this.audioStreamingEnded) {
      this.audioMessageEnded.next(true);
    }
  }

  private playAudioFromBase64(base64Data: string) {
    const cleanBase64Data = base64Data.replace(/\s/g, '');

    try {
      const byteString = atob(cleanBase64Data);
      const byteNumbers = new Array(byteString.length);
      for (let i = 0; i < byteString.length; i++) {
        byteNumbers[i] = byteString.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const audioBlob = new Blob([byteArray], { type: this.mimeType });
      const audioUrl = URL.createObjectURL(audioBlob);
      const audio = new Audio(audioUrl);

      this.isPlaying = true;
      this.currentAudio = audio;

      // Listen for the `ended` event to play the next chunk
      audio.addEventListener('ended', () => {
        this.isPlaying = false;
        this.playNextAudioChunk();
      });

      audio.play();
    } catch (error) {
      console.error('Failed to decode base64 string:', error);
      this.isPlaying = false;
      this.playNextAudioChunk(); // Try to play the next chunk if one fails
    }
  }

  /**
   * Stops the currently playing audio and clears the queue.
   */
  stopAudio() {
    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio.currentTime = 0;
      this.currentAudio = null;
    }
    this.audioQueue = [];
    this.isPlaying = false;
    this.audioMessageEnded.next(true);
  }

  public audioEnded(): Observable<boolean> {
    return this.audioMessageEnded.asObservable();
  }

  // Audio Section ends here

  public getMessages(): Observable<ChatResponseModel> {
    return this.messagesSubject.asObservable();
  }

  public endMessages(): Observable<string> {
    return this.endMessagesSubject.asObservable();
  }

  getChats(limit: number, page: number): Observable<any> {
    return this.http.get(
      `${environment.apiUrl}chat?limit=${limit}&page=${page}`
    );
  }

  getMessageFromId(
    chatId: string,
    limit: number,
    page: number
  ): Observable<any> {
    return this.http.get<messages>(
      `${environment.apiUrl}message?chat=${chatId}&limit=${limit}&page=${page}`
    );
  }

  addReactionTotheMessage(
    id: string,
    reaction: string
  ): Observable<any> {
    return this.http.post<messages>(
      `${environment.apiUrl}message/reaction`,
      { id, reaction }
    );
  }
  getMessageFromSharedId(
    chatId: string,
    limit: number,
    page: number
  ): Observable<any> {
    return this.http.get(
      `${environment.apiUrl}message/shared/list?chat=${chatId}&limit=${limit}&page=${page}`
    );
  }

  shareChat(chatId: string): Observable<any> {
    return this.http.post(`${environment.apiUrl}chat/share`, { chatId });
  }

  continueChat(chatId: string): Observable<any> {
    return this.http.post(`${environment.apiUrl}chat/share/continue`, {
      chatId,
    });
  }

  getGps() {
    if (isPlatformBrowser(this.platform) && navigator.geolocation) {
      const timeout = setTimeout(() => {
        console.warn('Geolocation request timed out.');
        // Resolve after timeout
      }, 5000); // 5 seconds timeout, adjust as needed

      navigator.geolocation.getCurrentPosition(
        (position: any) => {
          clearTimeout(timeout); // Clear the timeout if we get a position
          if (position) {
            this.latitude = position.coords.latitude;
            this.longitude = position.coords.longitude;
            this._sharedService.gpsSignal.set({
              latitude: position.coords.latitude,
              longitude: position.coords.longitude
            });
            this._sharedService.setLocalStorage(
              'latitude',
              position.coords.latitude
            );
            this._sharedService.setLocalStorage(
              'longitude',
              position.coords.longitude
            );
          }
          // Resolve the promise once GPS data is available
        },
        (error: any) => {
          clearTimeout(timeout); // Clear the timeout in case of error
          this._sharedService.removeFromLocalStorage('latitude');
          this._sharedService.removeFromLocalStorage('longitude');
          console.warn('Geolocation error or permission denied:', error);
          // Resolve the promise even if geolocation fails
        }
      );
    }
  }
  postArticleIdToGetChatId(articleId: string) {
    return this.http.post(`${environment.apiUrl}chat/article`, { articleId });
  }

  deleteChat(chatId: string): Observable<any> {
    return this.http.delete(`${environment.apiUrl}chat/${chatId}`);
  }
  getError(): Observable<string> {
    return this.answerError.asObservable();
  }
  getMyBagResponse(): Observable<MyBagModel[]> {
    return this.myBagResponseData.asObservable();
  }
  getMyBagError(): Observable<string> {
    return this.myBagError.asObservable();
  }
  getMyBagProcessingStatus(): Observable<boolean> {
    return this.isMyBagProcessRunning.asObservable();
  }
  checkOnlockCaddyFeature(): Observable<any> {
    return this.onlockCaddy.asObservable();
  }
  getLivkitToken(latitude: string, longitude: string, chatId: string): Observable<any> {
    return this.http.get(
      `${environment.apiUrl}live-kit/token`,
      { params: { chatId, location: `${latitude},${longitude}` } }
    );
  }
  startTranscription(token: string) {
    return this.http.post(
      `${environment.apiUrl}live-kit/transcription/start`,
      { token }
    );
  }
  getLatestChatId(): Observable<any> {
    return this.http.get<any>(`${environment.apiUrl}chat/latest-chat-id`);
  }
  emitIsMyBagProcessRunning() {
    if (this.socket) {
      this.socket.emit('is_my_bag_process_running', {});
    }
  }
}
